import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Permit2Store } from '@/features/actions/logic/permit2Store'
import { ActionHandler, ActionHandlerState } from '@/features/actions/logic/types'
import { parseWriteErrorMessage } from '@/features/actions/logic/utils'
import { JSONStringifyRich } from '@/utils/object'
import { toBigInt } from '@/utils/bigNumber'
import { MutationKey, useMutation } from '@tanstack/react-query'
import { maxUint256 } from 'viem'
import { useChainId, useConfig, useReadContract } from 'wagmi'
import { signTypedDataMutationOptions } from 'wagmi/query'
import { Permit2SignAction, PERMIT2_ADDRESS } from '../types'
import { permit2Abi, PERMIT_SINGLE_TYPE } from 'abis/evm/permit2'

export interface UseCreatePermit2SignHandlerOptions {
  enabled: boolean
  permit2Store: Permit2Store | undefined
}

export function useCreatePermit2SignHandler(
  action: Permit2SignAction,
  { enabled, permit2Store }: UseCreatePermit2SignHandlerOptions,
): ActionHandler {
  const { account } = useConnectedAddress()
  const chainId = useChainId()

  // Get the nonce from Permit2
  const { data: allowanceData, isLoading: isAllowanceLoading } = useReadContract({
    address: PERMIT2_ADDRESS,
    abi: permit2Abi,
    functionName: 'allowance',
    args: [account, action.token.address, action.spender],
    query: {
      enabled,
      gcTime: 0, // Don't cache nonce
    },
  })

  const nonce = allowanceData?.[2] ?? 0

  // Prepare the permit data
  const amount = toBigInt(action.token.toBaseUnit(action.amount))
  const sigDeadline = maxUint256 // No deadline (forever)

  const domain = {
    name: 'Permit2',
    chainId,
    verifyingContract: PERMIT2_ADDRESS,
  } as const

  const permitData = {
    details: {
      token: action.token.address,
      amount: amount > BigInt(2 ** 160 - 1) ? BigInt(2 ** 160 - 1) : amount, // Cap at uint160 max
      expiration: 0, // No expiration
      nonce,
    },
    spender: action.spender,
    sigDeadline,
  }

  const signTypedDataConfig = {
    domain,
    types: PERMIT_SINGLE_TYPE,
    primaryType: 'PermitSingle' as const,
    message: permitData,
  }

  const sign = useSignTypedData({
    mutation: {
      onSuccess: (signature) => {
        if (signature && permit2Store) {
          permit2Store.add({
            token: action.token,
            spender: action.spender,
            amount: permitData.details.amount,
            expiration: permitData.details.expiration,
            nonce: permitData.details.nonce,
            sigDeadline: permitData.sigDeadline,
            signature,
          })
        }
      },
    },
    mutationKey: getSignTypedDataMutationKey(signTypedDataConfig),
  })

  return {
    action,
    state: mapStatusesToActionState({ sign, isAllowanceLoading, enabled }),
    onAction: () => {
      sign.signTypedData(signTypedDataConfig)
    },
  }
}

interface MapStatusesToActionStateArgs {
  isAllowanceLoading: boolean
  sign: ReturnType<typeof useSignTypedData>
  enabled: boolean
}

function mapStatusesToActionState({
  isAllowanceLoading,
  sign,
  enabled,
}: MapStatusesToActionStateArgs): ActionHandlerState {
  if (!enabled) {
    return { status: 'disabled' }
  }

  if (sign.isPending || isAllowanceLoading) {
    return { status: 'loading' }
  }

  if (sign.status === 'error') {
    return { status: 'error', errorKind: 'tx-submission', message: parseWriteErrorMessage(sign.error!) }
  }

  if (sign.isSuccess) {
    return { status: 'success' }
  }

  return { status: 'ready' }
}

// Custom useSignTypedData hook with mutationKey support
import { UseSignTypedDataParameters, UseSignTypedDataReturnType } from 'wagmi'

function useSignTypedData(
  parameters: UseSignTypedDataParameters & { mutationKey?: MutationKey } = {},
): UseSignTypedDataReturnType {
  const { mutation, mutationKey } = parameters

  const config = useConfig(parameters)

  const mutationOptions = signTypedDataMutationOptions(config)
  const { mutate, mutateAsync, ...result } = useMutation({
    ...mutation,
    ...mutationOptions,
    mutationKey,
  })

  type Return = UseSignTypedDataReturnType
  return {
    ...result,
    signTypedData: mutate as Return['signTypedData'],
    signTypedDataAsync: mutateAsync as Return['signTypedDataAsync'],
  }
}

function getSignTypedDataMutationKey(config: unknown): MutationKey {
  return ['signTypedData', 'permit2', JSONStringifyRich(config)]
}

